import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, NgModule, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { HomeBNewsCardModule } from '@features/homeB/news-card/news-card.component';
import { News, NewsStatusEnum } from '@models/news/news';
import { SharedModule } from '@shared/shared.module';
import { WzbCarouselModule } from '@shared/wizbii-carousel/carousel.component';
import { NewsWebservice } from '@webservices/news-api/news.webservice';
import { WINDOW } from '@wizbii/angular-utilities';
import { SplideOptions } from '@wizbii/splide';
import { trackEventWithAtInternet } from '@wizbii/tracking';
import { EllipsisModule } from 'ngx-ellipsis';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-home-b-news-list',
  templateUrl: './news-list.component.html',
  styleUrls: ['./news-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeBNewsListComponent implements OnInit {
  readonly containerClass = 'news-carousel';

  selectedSlide$ = new BehaviorSubject<number>(0);
  carouselOptions$ = new BehaviorSubject<SplideOptions>({
    padding: '1.25rem',
    arrows: false,
    type: 'slide',
    perPage: 1,
    perMove: 4,
    gap: 35,
    autoplay: true,
    interval: 5000,
    pauseOnFocus: true,
    pauseOnHover: true,
    keyboard: 'focused',
    accessibility: true,
    trimSpace: true,
    slideFocus: true,
    classes: {
      pagination: 'splide__pagination news-carousel-pagination',
      page: 'splide__pagination__page news-carousel-page',
    },
  });

  news$: Observable<News[]> = this.getCurrentNews();
  vm$!: Observable<{
    news: News[];
    carouselOptions: SplideOptions;
    currentSlide: number;
    arrowPreviousIsDisabled: boolean;
    arrowNextIsDisabled: boolean;
  }>;

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }

  trackEvent = trackEventWithAtInternet;

  constructor(
    private readonly breakpointsService: BreakpointsService,
    private readonly newsWebservice: NewsWebservice,
    @Inject(WINDOW) private readonly window: any,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.vm$ = combineLatest([this.news$, this.carouselOptions$, this.selectedSlide$, this.isMobile$]).pipe(
      map(([news, options, currentSlide, isMobile]) => ({
        news,
        carouselOptions: {
          ...options,
          perMove: this.getPerMove({ currentSlide, perMove: this.carouselOptions$.value.perMove ?? 1, isMobile }),
          perPage: isMobile ? 1 : 4,
        },
        currentSlide,
        arrowPreviousIsDisabled: currentSlide <= 0,
        arrowNextIsDisabled: this.getArrowNextIsDisabled({
          currentSlide,
          perMove: this.carouselOptions$.value.perMove ?? 1,
          maxIndex: news.length,
          isMobile,
        }),
      }))
    );
  }

  getPerMove({
    currentSlide,
    isMobile,
    perMove,
  }: {
    currentSlide: number;
    isMobile: boolean;
    perMove: number;
  }): number {
    if (isMobile) {
      return 1;
    }
    return currentSlide % perMove === 0 ? perMove : currentSlide % perMove;
  }

  getArrowNextIsDisabled({
    currentSlide,
    perMove,
    maxIndex,
    isMobile,
  }: {
    currentSlide: number;
    perMove: number;
    maxIndex: number;
    isMobile: boolean;
  }): boolean {
    if (isMobile) {
      return currentSlide + 1 >= maxIndex;
    }
    return currentSlide + perMove >= maxIndex;
  }

  nextSplideSlide(currentIndex: number, perMove: number): void {
    this.selectedSlide$.next(currentIndex + perMove);
  }

  click({ objectID: label, externalUrl: url }: News): void {
    this.trackEvent({ category: 'Home B', action: 'Click Actualité', label, type: 'navigation' });
    if (url) {
      this.isInternalURL(url) ? this.router.navigate([new URL(url).pathname]) : this.window.open(url, '_blank');
    }
  }

  onSlideClicked([args]: any): void {
    this.news$.pipe(map((news) => news[args.index])).subscribe({ next: (news) => this.click(news) });
  }

  onSlideChange(index: number): void {
    this.selectedSlide$.next(index);
  }

  trackByNewsTitle(_: number, news: News): string {
    return news.title;
  }

  private isInternalURL(url: string): boolean {
    return url.startsWith('https://www.1jeune1solution');
  }

  private getCurrentNews(): Observable<News[]> {
    const algoliaFormattedTimestamp = Date.now() / 1000;
    return this.newsWebservice
      .getNews('', {
        filters: `status:${NewsStatusEnum.Online} AND startDate < ${algoliaFormattedTimestamp} AND endDate > ${algoliaFormattedTimestamp}`,
      })
      .pipe(map((result) => result.hits));
  }
}

@NgModule({
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    EllipsisModule,
    SharedModule,
    HomeBNewsCardModule,
    WzbCarouselModule,
  ],
  declarations: [HomeBNewsListComponent],
  exports: [HomeBNewsListComponent],
})
export class HomeBNewsListModule {}
